import { annotationSizeMm, ANNOT_COLOR } from './annotations'
import { arcSweep, walkPath } from './arcFit'
import {
  dimensionGeometry,
  dimensionSizeMm,
  helveticaWidthMm,
  DIM_COLOR,
  DIM_DASH_MM,
  DIM_TICK_MM,
  DIM_WIDTH_MM,
} from './dimensions'
import { fillGroups, fmt, layerColors, layerFill, normalizeHex } from './drawingToSvg'
import { ESPACAMENTO_MM, TRACO_DA_TRAMA_MM, tramaNaCaixa, type EstiloDoCorteNoPapel } from './tramaNoPapel'
import type { Arc2D, Drawing2D, Point2, Polyline2D } from './viewTypes'

// ---------------------------------------------------------------------------
// Writer PDF mínimo, escrito à mão (ZERO dependências novas).
//
// PDF 1.4 com cinco objectos: catálogo, árvore de páginas, página,
// content stream (NÃO comprimido) e a fonte base-14 Helvetica para o
// rodapé. O content stream usa só os operadores de caminho e traço
// (`m`, `l`, `h`, `S`), largura (`w`), cor de traço (`RG`) e texto
// (`BT`/`Tf`/`Td`/`Tj`/`ET`).
//
// Porquê à mão: o requisito é não acrescentar dependências, e a fatia de
// PDF de que precisamos (linhas + uma linha de texto) é pequena e
// verificável. A tabela xref é construída com os deslocamentos REAIS em
// bytes; como só se escrevem caracteres latin-1 (código <= 255), o
// comprimento da string em caracteres é igual ao comprimento em bytes e
// os deslocamentos podem ser medidos enquanto se compõe o ficheiro.
//
// Escala real: 1 m de modelo = 1000/N mm de papel. O desenho é centrado
// na área útil (papel menos margens menos a faixa do rodapé). O sistema
// de coordenadas do PDF tem origem em baixo à esquerda e y para cima,
// tal como o desenho, por isso não há inversão de eixo (ao contrário do
// SVG).
// ---------------------------------------------------------------------------

export type PaperName = 'A4' | 'A3'

export interface PdfOptions {
  paper?: PaperName
  landscape?: boolean
  /** Escala de papel; 'fit' escolhe a escala standard mais aproximada
   *  que caiba na folha. Por omissão usa a do desenho. */
  scaleDenom?: number | 'fit'
  /** Margem, em mm. */
  marginMm?: number
  cutWidthMm?: number
  projWidthMm?: number
  /** Quanto a cor da projecção se aproxima do branco (ver
   *  `lineWeights.ts`). Por omissão `SVG_PROJ_FADE`. */
  projFade?: number
  /** Data do rodapé (ISO curta). Parametrizável para testes
   *  determinísticos; por omissão é a data de hoje. */
  date?: string
  /** Desliga o rodapé. */
  footer?: boolean
  /** Cor do corte quando a camada não traz cor. */
  cutColor?: string
  /** Preenche (poché) os contornos FECHADOS do corte, com o operador de
   *  preenchimento par-ímpar (`f*`), para os vãos ficarem furos.
   *  Desligado por omissão, como no writer SVG. */
  fillCuts?: boolean
  /** O estilo do corte de cada elemento (fundo e trama), o mesmo do 3D e
   *  do SVG. Uma camada com cor de preenchimento escolhida à mão fica com
   *  ela. */
  estiloDoCorte?: EstiloDoCorteNoPapel
  /** Cor do texto das anotações. */
  annotColor?: string
  /** Cor do traço e do texto das cotas. */
  dimColor?: string
}

/** Milímetros por polegada / pontos por polegada. */
const MM_TO_PT = 72 / 25.4

/** Papéis em milímetros (retrato). */
export const PAPER_MM: Record<PaperName, [number, number]> = {
  A4: [210, 297],
  A3: [297, 420],
}

/** Escalas normalizadas de desenho de arquitectura e engenharia. */
export const STANDARD_SCALES = [
  1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000,
]

/** Altura da faixa do rodapé, em mm. */
const FOOTER_MM = 8

export interface PdfLayout {
  scaleDenom: number
  /** Dimensões da folha em pontos. */
  widthPt: number
  heightPt: number
  /** Origem do desenho (canto inferior esquerdo do bbox) em pontos. */
  originXPt: number
  originYPt: number
  /** Pontos de papel por metro de modelo. */
  ptPerMeter: number
  /** true quando nem a escala mais pequena da lista chegava e se usou
   *  uma escala calculada. */
  fitted: boolean
  /** true quando o desenho, a esta escala, excede a área útil da folha:
   *  o PDF sairia com as bordas cortadas. */
  overflow: boolean
  /** Escala normalizada mais pequena em que o desenho cabe nesta folha
   *  (null quando nem a última da lista chega). Serve para a mensagem
   *  de aviso propor uma alternativa concreta. */
  fitScaleDenom: number | null
}

/** Calcula a paginação: escala, dimensão da folha e canto do desenho. */
export function pdfLayout(d: Drawing2D, opts?: PdfOptions): PdfLayout {
  const paper = PAPER_MM[opts?.paper ?? 'A4']
  const landscape = opts?.landscape ?? false
  const wMm = landscape ? paper[1] : paper[0]
  const hMm = landscape ? paper[0] : paper[1]
  const margin = opts?.marginMm ?? 10
  const footerMm = opts?.footer === false ? 0 : FOOTER_MM
  const availW = Math.max(wMm - margin * 2, 1)
  const availH = Math.max(hMm - margin * 2 - footerMm, 1)
  const dwM = Math.max(d.bbox.maxX - d.bbox.minX, 0)
  const dhM = Math.max(d.bbox.maxY - d.bbox.minY, 0)

  let scaleDenom: number
  let fitted = false
  const requested = opts?.scaleDenom ?? d.scaleDenom ?? 100
  // Escala em mm/m: 1000/N. Cabe quando dw*1000/N <= availW.
  const needed = Math.max(
    (dwM * 1000) / availW,
    (dhM * 1000) / availH,
    Number.MIN_VALUE,
  )
  const fitScale = STANDARD_SCALES.find((n) => n >= needed) ?? null
  if (requested === 'fit') {
    const found = fitScale
    if (found) {
      scaleDenom = found
    } else {
      scaleDenom = Math.ceil(needed)
      fitted = true
    }
  } else {
    scaleDenom = Number.isFinite(requested) && requested > 0 ? requested : 100
  }

  const mmPerMeter = 1000 / scaleDenom
  const drawWmm = dwM * mmPerMeter
  const drawHmm = dhM * mmPerMeter
  const originXmm = margin + (availW - drawWmm) / 2
  const originYmm = margin + footerMm + (availH - drawHmm) / 2
  // Folga de 1 µm de papel: um desenho que cabe à justa não é aviso.
  const OVERFLOW_EPS_MM = 1e-3
  const overflow =
    drawWmm > availW + OVERFLOW_EPS_MM || drawHmm > availH + OVERFLOW_EPS_MM

  return {
    scaleDenom,
    widthPt: wMm * MM_TO_PT,
    heightPt: hMm * MM_TO_PT,
    originXPt: originXmm * MM_TO_PT,
    originYPt: originYmm * MM_TO_PT,
    ptPerMeter: mmPerMeter * MM_TO_PT,
    fitted,
    overflow,
    fitScaleDenom: fitScale,
  }
}

/** Aviso de que o desenho não cabe na folha, com a alternativa
 *  concreta. String vazia quando cabe. */
export function pdfOverflowMessage(layout: PdfLayout, paper: PaperName): string {
  if (!layout.overflow) return ''
  const alt =
    layout.fitScaleDenom !== null
      ? `Escolhe 1:${layout.fitScaleDenom}, uma folha maior ou "Ajustar à folha".`
      : 'Escolhe "Ajustar à folha".'
  return (
    `O desenho não cabe em ${paper} a 1:${layout.scaleDenom}: ` +
    `o PDF sai cortado nas bordas. ${alt}`
  )
}

/** O mesmo desenho com a escala que vai mesmo para o papel.
 *
 *  A escala vive em dois sítios: a da vista (fixada quando se gerou o
 *  desenho) e a escolhida na barra. O PDF sempre usou a da barra; o SVG
 *  e o DXF liam a da vista. Passar todos os writers por aqui garante que
 *  os três ficheiros saem à mesma escala, incluindo a resolvida pelo
 *  "Ajustar à folha". */
export function drawingAtPaperScale(d: Drawing2D, opts?: PdfOptions): Drawing2D {
  const scaleDenom = pdfLayout(d, opts).scaleDenom
  return scaleDenom === d.scaleDenom ? d : { ...d, scaleDenom }
}

/** Gera o PDF de um desenho. Devolve os bytes do ficheiro. */
export function drawingToPdf(d: Drawing2D, opts?: PdfOptions): Uint8Array {
  const layout = pdfLayout(d, opts)
  const cutW = (opts?.cutWidthMm ?? 0.35) * MM_TO_PT
  const projW = (opts?.projWidthMm ?? 0.13) * MM_TO_PT
  const cutColor = opts?.cutColor ?? '#111111'
  const withFooter = opts?.footer !== false

  // Numéricas primeiro: as cotas somam afastamentos de papel ao ponto já
  // convertido (e o PDF, ao contrário do SVG, não tem `text-anchor`:
  // centrar um valor obriga a fazer contas com a largura do texto).
  const ptX = (x: number): number =>
    layout.originXPt + (x - d.bbox.minX) * layout.ptPerMeter
  const ptY = (y: number): number =>
    layout.originYPt + (y - d.bbox.minY) * layout.ptPerMeter
  const px = (x: number): string => fmt(ptX(x), 2)
  const py = (y: number): string => fmt(ptY(y), 2)

  const ops: string[] = []
  ops.push('q')
  ops.push('1 J 1 j') // extremidades e junções redondas
  /** Um segmento de Bézier cúbica do arco, do ângulo `t0` ao `t1`
   *  (amplitude <= 90 graus). k = 4/3 * tan(dt/4) é a constante clássica:
   *  para 90 graus dá 0,5523. */
  const bezier = (
    c: { cx: number; cy: number; r: number },
    t0: number,
    t1: number,
  ): void => {
    const dt = t1 - t0
    const kk = (4 / 3) * Math.tan(dt / 4)
    const p0: Point2 = [c.cx + c.r * Math.cos(t0), c.cy + c.r * Math.sin(t0)]
    const p3: Point2 = [c.cx + c.r * Math.cos(t1), c.cy + c.r * Math.sin(t1)]
    const c1: Point2 = [
      p0[0] - kk * c.r * Math.sin(t0),
      p0[1] + kk * c.r * Math.cos(t0),
    ]
    const c2: Point2 = [
      p3[0] + kk * c.r * Math.sin(t1),
      p3[1] - kk * c.r * Math.cos(t1),
    ]
    ops.push(
      `${px(c1[0])} ${py(c1[1])} ${px(c2[0])} ${py(c2[1])} ${px(p3[0])} ${py(
        p3[1],
      )} c`,
    )
  }
  /** Arco em Béziers, partido de 90 em 90 graus. */
  const arcOps = (arc: Arc2D): void => {
    const total = arcSweep(arc) * (arc.ccw ? 1 : -1)
    const parts = Math.max(1, Math.ceil(Math.abs(total) / (Math.PI / 2)))
    const step = total / parts
    for (let i = 0; i < parts; i++) {
      bezier(arc, arc.a0 + step * i, arc.a0 + step * (i + 1))
    }
  }
  /** Círculo completo: quatro Béziers de 90 graus. */
  const circleOps = (c: { cx: number; cy: number; r: number }): void => {
    ops.push(`${px(c.cx + c.r)} ${py(c.cy)} m`)
    for (let i = 0; i < 4; i++) {
      bezier(c, (Math.PI / 2) * i, (Math.PI / 2) * (i + 1))
    }
    ops.push('h')
  }
  /** Compõe o caminho de uma polilinha (sem operador de pintura). */
  const emitPath = (pl: Polyline2D): boolean => {
    if (pl.circle) {
      circleOps(pl.circle)
      return true
    }
    const walk = walkPath(pl)
    if (!walk) return false
    ops.push(`${px(walk.from[0])} ${py(walk.from[1])} m`)
    for (const step of walk.steps) {
      if (step.kind === 'arc') arcOps(step.arc)
      else ops.push(`${px(step.to[0])} ${py(step.to[1])} l`)
    }
    if (pl.closed) ops.push('h')
    return true
  }
  const path = (pl: Polyline2D): void => {
    if (pl.points.length < 2) return
    if (emitPath(pl)) ops.push('S')
  }
  /** Sub-caminho fechado, sem operador de pintura (a pintura vem no fim
   *  do grupo, com o `f*`). */
  const subpath = (pl: Polyline2D): void => {
    if (pl.points.length < 3) return
    if (pl.circle) {
      circleOps(pl.circle)
      return
    }
    const walk = walkPath(pl)
    if (!walk) return
    ops.push(`${px(walk.from[0])} ${py(walk.from[1])} m`)
    for (const step of walk.steps) {
      if (step.kind === 'arc') arcOps(step.arc)
      else ops.push(`${px(step.to[0])} ${py(step.to[1])} l`)
    }
    ops.push('h')
  }

  // Manchas de todas as camadas primeiro (senão a mancha de uma camada
  // tapava os traços da anterior), depois os traços.
  if (opts?.fillCuts) {
    for (const layer of d.layers) {
      const groups = fillGroups(layer)
      if (groups.length === 0) continue
      ops.push(`${rgbOps(layerFill(layer, cutColor))} rg`)
      const camadaEscolhida = normalizeHex(layer.fillHex) !== undefined
      for (const grp of groups) {
        const estilo = !camadaEscolhida && opts?.estiloDoCorte ? opts.estiloDoCorte(grp.localId, layer.name) : null
        if (estilo) {
          // O PDF não tem padrões simples: recorta-se pelo contorno (par-
          // ímpar, os vãos ficam de fora), pinta-se o fundo e desenha-se a
          // trama por cima, dentro do recorte.
          let x0 = Infinity
          let y0 = Infinity
          let x1 = -Infinity
          let y1 = -Infinity
          for (const pl of grp.polys) {
            for (const [x, y] of pl.points) {
              x0 = Math.min(x0, ptX(x))
              x1 = Math.max(x1, ptX(x))
              y0 = Math.min(y0, ptY(y))
              y1 = Math.max(y1, ptY(y))
            }
          }
          if (!Number.isFinite(x0)) continue
          // Folga para os arcos, que podem passar além dos vértices.
          const folga = 2
          ops.push('q')
          for (const pl of grp.polys) subpath(pl)
          ops.push('W* n')
          ops.push(`${rgbOps(estilo.fundo)} rg`)
          ops.push(`${fmt(x0 - folga, 2)} ${fmt(y0 - folga, 2)} ${fmt(x1 - x0 + folga * 2, 2)} ${fmt(y1 - y0 + folga * 2, 2)} re f`)
          if (estilo.trama !== 'cheio') {
            const t = tramaNaCaixa(
              estilo.trama,
              { x0: x0 - folga, y0: y0 - folga, x1: x1 + folga, y1: y1 + folga },
              ESPACAMENTO_MM * MM_TO_PT,
            )
            ops.push(`${fmt(TRACO_DA_TRAMA_MM * MM_TO_PT, 3)} w`)
            ops.push(`${rgbOps(estilo.corDaTrama)} RG`)
            for (const [ax, ay, bx, by] of t.linhas) ops.push(`${fmt(ax, 2)} ${fmt(ay, 2)} m ${fmt(bx, 2)} ${fmt(by, 2)} l S`)
            if (t.pontos.length > 0) {
              ops.push(`${rgbOps(estilo.corDaTrama)} rg`)
              for (const [cx, cy, r] of t.pontos) ops.push(`${fmt(cx - r, 2)} ${fmt(cy - r, 2)} ${fmt(r * 2, 2)} ${fmt(r * 2, 2)} re f`)
            }
          }
          ops.push('Q')
          continue
        }
        for (const pl of grp.polys) subpath(pl)
        // `f*` = preencher com a regra PAR-ÍMPAR: os contornos do mesmo
        // elemento estão todos no mesmo caminho, por isso um anel dentro
        // de outro (vão numa parede) sai furado.
        ops.push('f*')
      }
    }
  }
  for (const layer of d.layers) {
    const colors = layerColors(layer, cutColor, opts?.projFade)
    if (layer.projection.length > 0) {
      ops.push(`${fmt(projW, 3)} w`)
      ops.push(`${rgbOps(colors.proj)} RG`)
      for (const pl of layer.projection) path(pl)
    }
    if (layer.cut.length > 0) {
      ops.push(`${fmt(cutW, 3)} w`)
      ops.push(`${rgbOps(colors.cut)} RG`)
      for (const pl of layer.cut) path(pl)
    }
  }

  // Cotas: traço, extensões e o valor RODADO sobre a linha. A rotação
  // faz-se com a matriz de texto (`Tm`), que é a maneira do PDF: não há
  // operador de rotação de texto, há uma matriz. O sistema do PDF tem o
  // y para cima, tal como o desenho, portanto os ângulos vão directos
  // (ao contrário do SVG, onde trocam de sinal).
  const dims = d.dimensions ?? []
  if (dims.length > 0) {
    const dimColor = opts?.dimColor ?? DIM_COLOR
    ops.push(`${fmt(DIM_WIDTH_MM * MM_TO_PT, 3)} w`)
    ops.push(`${rgbOps(dimColor)} RG`)
    for (const dim of dims) {
      const g = dimensionGeometry(dim)
      for (const ln of g.lines) {
        ops.push(`${px(ln.a[0])} ${py(ln.a[1])} m`)
        ops.push(`${px(ln.b[0])} ${py(ln.b[1])} l`)
        ops.push('S')
      }
      for (const t of g.ticks) {
        const cx = ptX(t.at[0])
        const cy = ptY(t.at[1])
        const dx = t.nx * DIM_TICK_MM * MM_TO_PT
        const dy = t.ny * DIM_TICK_MM * MM_TO_PT
        ops.push(`${fmt(cx - dx, 2)} ${fmt(cy - dy, 2)} m`)
        ops.push(`${fmt(cx + dx, 2)} ${fmt(cy + dy, 2)} l`)
        ops.push('S')
      }
      if (g.dashed.length > 0) {
        // `d` = padrão de tracejado; repõe-se a contínuo no fim para não
        // contaminar o que vier depois.
        ops.push(
          `[${fmt(DIM_DASH_MM[0] * MM_TO_PT, 2)} ${fmt(
            DIM_DASH_MM[1] * MM_TO_PT,
            2,
          )}] 0 d`,
        )
        for (const ln of g.dashed) {
          ops.push(`${px(ln.a[0])} ${py(ln.a[1])} m`)
          ops.push(`${px(ln.b[0])} ${py(ln.b[1])} l`)
          ops.push('S')
        }
        ops.push('[] 0 d')
      }
      if (g.texts.length > 0) {
        const size = dimensionSizeMm(dim)
        ops.push(`${rgbOps(dimColor)} rg`)
        ops.push('BT')
        ops.push(`/F1 ${fmt(size * MM_TO_PT, 2)} Tf`)
        for (const t of g.texts) {
          const c = Math.cos(t.angleRad)
          const s = Math.sin(t.angleRad)
          // Centrar: recuar meia largura do texto ao longo da direcção
          // em que ele se escreve.
          const half = (helveticaWidthMm(t.text, size) / 2) * MM_TO_PT
          const off = t.offsetMm * MM_TO_PT
          const bx = ptX(t.at[0]) + t.nx * off - c * half
          const by = ptY(t.at[1]) + t.ny * off - s * half
          ops.push(
            `${fmt(c, 5)} ${fmt(s, 5)} ${fmt(-s, 5)} ${fmt(c, 5)} ${fmt(
              bx,
              2,
            )} ${fmt(by, 2)} Tm`,
          )
          ops.push(`(${pdfString(t.text)}) Tj`)
        }
        ops.push('ET')
      }
    }
  }

  // Anotações: mesma fonte base-14 do rodapé (o recurso /F1 já está
  // declarado na página, não custa nenhum objecto novo). A cota vem em
  // mm de PAPEL, portanto é independente da escala do desenho, tal como
  // no SVG.
  const notes = d.annotations ?? []
  if (notes.length > 0) {
    ops.push(`${rgbOps(opts?.annotColor ?? ANNOT_COLOR)} rg`)
    ops.push('BT')
    for (const a of notes) {
      ops.push(`/F1 ${fmt(annotationSizeMm(a) * MM_TO_PT, 2)} Tf`)
      // `Td` é RELATIVO à linha anterior dentro do mesmo BT/ET: usa-se
      // `Tm` (matriz de texto absoluta) para cada etiqueta ficar
      // ancorada ao seu ponto sem depender da anterior.
      ops.push(`1 0 0 1 ${px(a.x)} ${py(a.y)} Tm`)
      ops.push(`(${pdfString(a.text)}) Tj`)
    }
    ops.push('ET')
  }
  ops.push('Q')

  if (withFooter) {
    const date = opts?.date ?? new Date().toISOString().slice(0, 10)
    const text = `${d.name} | 1:${layout.scaleDenom} | ${date}`
    const margin = (opts?.marginMm ?? 10) * MM_TO_PT
    ops.push('q')
    ops.push('0.2 0.2 0.2 rg')
    ops.push('BT')
    ops.push('/F1 8 Tf')
    ops.push(`${fmt(margin, 2)} ${fmt(margin, 2)} Td`)
    ops.push(`(${pdfString(text)}) Tj`)
    ops.push('ET')
    ops.push('Q')
  }

  const content = ops.join('\n') + '\n'

  // --- montagem do ficheiro com deslocamentos reais -------------------------
  const objects: string[] = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${fmt(
      layout.widthPt,
      2,
    )} ${fmt(layout.heightPt, 2)}] /Resources << /Font << /F1 5 0 R >> >>` +
      ' /Contents 4 0 R >>',
    `<< /Length ${content.length} >>\nstream\n${content}endstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica' +
      ' /Encoding /WinAnsiEncoding >>',
  ]

  let pdf = '%PDF-1.4\n'
  // Comentário binário: convenciona-se para as ferramentas tratarem o
  // ficheiro como binário.
  pdf += '%âãÏÓ\n'
  const offsets: number[] = []
  objects.forEach((body, i) => {
    offsets.push(pdf.length)
    pdf += `${i + 1} 0 obj\n${body}\nendobj\n`
  })
  const xrefOffset = pdf.length
  const count = objects.length + 1
  pdf += `xref\n0 ${count}\n`
  pdf += '0000000000 65535 f \n'
  for (const off of offsets) {
    pdf += `${String(off).padStart(10, '0')} 00000 n \n`
  }
  pdf += `trailer\n<< /Size ${count} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`

  return latin1Bytes(pdf)
}

/** Componentes RGB 0..1 de um hex, prontas para o operador RG/rg. */
function rgbOps(hex: string): string {
  const c = normalizeHex(hex) ?? '#000000'
  const n = Number.parseInt(c.slice(1), 16)
  const r = ((n >> 16) & 0xff) / 255
  const g = ((n >> 8) & 0xff) / 255
  const b = (n & 0xff) / 255
  return `${fmt(r, 3)} ${fmt(g, 3)} ${fmt(b, 3)}`
}

/** Escapa uma cadeia literal de PDF e reduz o texto a WinAnsi: os
 *  caracteres acima de 255 (que não cabem num byte) passam a '?'. */
export function pdfString(s: string): string {
  let out = ''
  for (const ch of String(s)) {
    const code = ch.codePointAt(0) ?? 63
    if (ch === '(' || ch === ')' || ch === '\\') out += '\\' + ch
    else if (code === 10) out += '\\n'
    else if (code === 13) out += '\\r'
    else if (code > 255) out += '?'
    else out += ch
  }
  return out
}

function latin1Bytes(s: string): Uint8Array {
  const out = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff
  return out
}
